import { assign, createMachine } from 'xstate';

import { sendGAEventToDataLayer } from '@nw/shared/utils';

import {
  USER_AUTH_HANDLER_MACHINE_ID,
  userAuthHandlerMachineActions,
  userAuthHandlerMachineEvents,
  userAuthHandlerMachineGuards,
  userAuthHandlerMachineServices,
  userAuthHandlerMachineStates
} from '../../constants/userAuthHandlerMachineConstants';
import {
  UserAuthHandlerMachineContext,
  UserAuthHandlerMachineEvent
} from '../../types/UserAuthHandlerMachineTypes';

const states = userAuthHandlerMachineStates;
const actions = userAuthHandlerMachineActions;
const services = userAuthHandlerMachineServices;
const events = userAuthHandlerMachineEvents;
const guards = userAuthHandlerMachineGuards;

export const UserAuthHandlerMachine = () =>
  createMachine<UserAuthHandlerMachineContext, UserAuthHandlerMachineEvent>({
    id: USER_AUTH_HANDLER_MACHINE_ID,
    initial: states.getClientConfigDetailsState,
    context: {
      initialURL: '',
      clientConfigDetails: null,
      authCode: '',
      paymentAuthCode: '',
      formsAuthCode: '',
      inviteCode: '',
      callBackURL: '',
      clientID: '',
      authClientID: '',
      fallBackURL: '',
      isLoggedIn: false,
      authMachineRef: null,
      values: {},
      shouldRedirectToProfilePage: false,
      errorMessage: '',
      isAuthButtonLoading: false,
      isOtpSentToUser: false,
      isNewUser: false,
      shouldDoChildMachineSelfTransition: false,
      shouldDoSelfTransition: false,
      childContext: {},
      userProfileData: null
    },
    on: {
      [events.assignValuesToContext]: {
        actions: [actions.assignValuesToContext]
      },
      [events.onSuccessOtpVerification]: {
        actions: [
          actions.setLoginAccessAndRefreshToken,
          actions.onSuccessOtpVerification
        ],
        target: states.getClientAuthCodeState
      },
      [events.onClickBackButton]: {
        actions: [
          actions.resetOtp,
          actions.setIsAuthButtonLoadingToFalse,
          actions.clearErrorMessageFromContext,
          actions.updateIsOtpSentToUserFalse
        ],
        target: states.notLoggedInUserState
      },
      [events.onEntryGetOTP]: {
        actions: [
          () => {
            sendGAEventToDataLayer({
              event: 'get_otp'
            });
          },
          actions.setIsAuthButtonLoadingToTrue,
          actions.clearErrorMessageFromContext,
          actions.resetOtp
        ]
      },
      [events.onSuccessGetOTP]: {
        actions: [
          actions.updateIsOtpSentToUserTrue,
          actions.setIsAuthButtonLoadingToFalse
        ]
      },
      [events.onClickResendOtp]: {
        actions: [
          () => {
            sendGAEventToDataLayer({
              event: 'resend_otp'
            });
          }
        ]
      },
      [events.onErrorGetOTP]: {
        actions: [
          actions.assignErrorMessageToContext,
          actions.assignChildMachineSelfTransitionToTrue,
          actions.setIsAuthButtonLoadingToFalse
        ],
        target: states.notLoggedInUserState
      },
      [events.doChildMachineSelfTransition]: {
        actions: [
          actions.assignChildMachineSelfTransitionToFalse,
          actions.doChildMachineSelfTransition
        ]
      },
      [events.onSuccessGetSignUpOTP]: {
        actions: [
          actions.updateIsOtpSentToUserTrue,
          actions.updateIsNewUserInContextToTrue,
          actions.setIsAuthButtonLoadingToFalse
        ]
      },
      [events.resetAuthMachine]: {
        actions: [
          assign((context, event) => {
            return {
              authMachineRef: null,
              authCode: '',
              errorMessage: '',
              isOtpSentToUser: false,
              isNewUser: false,
              shouldDoChildMachineSelfTransition: false,
              shouldDoSelfTransition: false,
              childContext: {},
              userProfileData: null
            };
          })
        ],
        target: states.notLoggedInUserState
      }
    },
    states: {
      [states.getClientConfigDetailsState]: {
        entry: [
          actions.assignQueryParamsToContext,
          actions.assignLocalConfigClientIDToContext
        ],
        invoke: {
          src: services.getClientConfigDetailsApi,
          onDone: {
            actions: [actions.assignClientConfigDetailsToContext],
            target: states.notLoggedInUserState
          },
          onError: {
            actions: [actions.handleErrorOnFailureGetClientConfigDetails]
          }
        }
      },
      [states.notLoggedInUserState]: {
        entry: [actions.setIsAuthButtonLoadingToFalse],
        on: {
          [events.onClickGetOtpButton]: {
            actions: [actions.assignValuesToContext],
            target: states.initializeAuthMachineState
          }
        }
      },
      [states.initializeAuthMachineState]: {
        entry: [actions.assignAuthMachineRefToContext]
      },

      [states.getClientAuthCodeState]: {
        entry: [
          actions.setIsAuthButtonLoadingToTrue,
          actions.resetOtp,
          actions.clearErrorMessageFromContext
        ],
        invoke: {
          src: services.getClientAuthCode,
          onDone: [
            {
              cond: guards.shouldFetchPaymentAuthCode,
              actions: [actions.onSuccessGettingClientAuthCode],
              target: states.getPaymentAuthCodeState
            },
            {
              cond: guards.shouldCallUserProfileDetails,
              actions: [actions.onSuccessGettingClientAuthCode],
              target: states.getUserProfileDetailsState
            },
            {
              actions: [actions.onSuccessGettingClientAuthCode],
              target: states.userLoginSuccessState
            }
          ],
          onError: {
            actions: [
              actions.setIsAuthButtonLoadingToFalse,
              actions.assignErrorMessageToContext,
              actions.assignSelfTransitionToTrue
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            actions: [actions.assignSelfTransitionToFalse],
            target: states.getClientAuthCodeState
          }
        }
      },
      [states.getPaymentAuthCodeState]: {
        invoke: {
          src: services.getPaymentAuthCode,
          onDone: {
            actions: [actions.onSuccessGettingPaymentAuthCode],
            target: states.getPaymentsClientAuthenticationDetailsState
          },
          onError: {
            actions: [
              actions.setIsAuthButtonLoadingToFalse,
              actions.assignErrorMessageToContext,
              actions.assignSelfTransitionToTrue
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            actions: [actions.assignSelfTransitionToFalse],
            target: states.getPaymentAuthCodeState
          }
        }
      },
      [states.getPaymentsClientAuthenticationDetailsState]: {
        invoke: {
          src: services.getPaymentClientAuthenticationDetails,
          onDone: [
            {
              actions: [
                actions.onSuccessGettingPaymentsClientAuthenticationDetails
              ],
              cond: guards.shouldFetchFormAuthCode,
              target: states.getFormAuthCode
            },
            {
              actions: [
                actions.onSuccessGettingPaymentsClientAuthenticationDetails
              ],
              cond: guards.shouldCallUserProfileDetails,
              target: states.getUserProfileDetailsState
            },
            {
              actions: [
                actions.onSuccessGettingPaymentsClientAuthenticationDetails,
                actions.setIsAuthButtonLoadingToFalse
              ],
              target: states.userLoginSuccessState
            }
          ],
          onError: {
            actions: [
              actions.setIsAuthButtonLoadingToFalse,
              actions.assignErrorMessageToContext,
              actions.assignSelfTransitionToTrue
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            actions: [actions.assignSelfTransitionToFalse],
            target: states.getPaymentsClientAuthenticationDetailsState
          }
        }
      },
      [states.getFormAuthCode]: {
        invoke: {
          src: services.getFormAuthCode,
          onDone: {
            actions: [actions.onSuccessGettingFormsAuthCode],
            target: states.getFormClientAuthDetailsState
          },
          onError: {
            actions: [
              actions.setIsAuthButtonLoadingToFalse,
              actions.assignErrorMessageToContext,
              actions.assignSelfTransitionToTrue
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            actions: [actions.assignSelfTransitionToFalse],
            target: states.getFormAuthCode
          }
        }
      },
      [states.getFormClientAuthDetailsState]: {
        invoke: {
          src: services.getFormClientAuthenticationDetails,
          onDone: [
            {
              actions: [actions.onSuccessGettingFormsClientAuthDetails],
              cond: guards.shouldCallUserProfileDetails,
              target: states.getUserProfileDetailsState
            },
            {
              actions: [
                actions.onSuccessGettingFormsClientAuthDetails,
                actions.setIsAuthButtonLoadingToFalse
              ],
              target: states.userLoginSuccessState
            }
          ],
          onError: {
            actions: [
              actions.setIsAuthButtonLoadingToFalse,
              actions.assignErrorMessageToContext,
              actions.assignSelfTransitionToTrue
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            actions: [actions.assignSelfTransitionToFalse],
            target: states.getFormClientAuthDetailsState
          }
        }
      },
      [states.getUserProfileDetailsState]: {
        invoke: {
          src: services.getUserProfileDetails,
          onDone: {
            actions: [actions.onSuccessGettingUserProfileDetails],
            target: states.userLoginSuccessState
          },
          onError: {
            actions: [
              actions.setIsAuthButtonLoadingToFalse,
              actions.assignErrorMessageToContext,
              actions.assignSelfTransitionToTrue
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            actions: [actions.assignSelfTransitionToFalse],
            target: states.getUserProfileDetailsState
          }
        }
      },
      [states.userLoginSuccessState]: {
        entry: [
          actions.setIsUserLoggedInToTrue,
          actions.assignChildContextToParentContext
        ],
        on: {
          '': [
            {
              cond: guards.isCallBackUrlPresent,
              actions: [actions.redirectToCallBackUrl],
              target: states.finalState
            },
            {
              cond: guards.isFallBackUrlPresent,
              actions: [actions.redirectToFallBackUrl],
              target: states.finalState
            },
            { actions: [actions.redirectToHomePage], target: states.finalState }
          ]
        }
      },
      [states.finalState]: {}
    }
  });
